import React, { useState, useEffect } from 'react';
import {
    EmployeeIcon, ListChecksIcon, CalendarDaysIcon,
    ClockIcon, EmployeesIcon, MonitorIcon, MailIcon, BellIcon, DatabaseIcon
} from '../components/icons';
import EmployeesSettingsTab from './EmployeesSettingsTab';
import ClassificationsTab from '../components/ClassificationsTab';
import DisplayGroupsSettingsTab from '../components/DisplayGroupsSettingsTab';
import PayrollSettingsTab from '../components/PayrollSettingsTab';
import ImportExportTab from '../components/ImportExportTab';
import SchedulesSettingsTab from './SchedulesSettingsTab';
import ScreensaverSettingsTab from '../components/ScreensaverSettingsTab';
import SMTPSettingsTab from '../components/SMTPSettingsTab';
import NotificationSettingsTab from '../components/NotificationSettingsTab';

interface ConfigNavItemProps {
    icon: React.ReactNode;
    label: string;
    isActive: boolean;
    onClick: () => void;
}

const ConfigNavItem: React.FC<ConfigNavItemProps> = ({ icon, label, isActive, onClick }) => {
    const baseClasses = 'flex items-center gap-3 w-full text-left p-3 rounded-lg transition-colors text-gray-700 dark:text-gray-200';
    const activeClasses = 'bg-brand-secondary text-white font-semibold';
    const inactiveClasses = 'hover:bg-gray-200 dark:hover:bg-gray-700';

    return (
        <button onClick={onClick} className={`${baseClasses} ${isActive ? activeClasses : inactiveClasses}`}>
            <div className="w-6 h-6">{icon}</div>
            <span>{label}</span>
        </button>
    );
};

const NAV_SECTIONS = {
    "Employees": [
        { view: 'employees', label: 'Employees', icon: <EmployeeIcon className="w-6 h-6" /> },
        { view: 'classifications', label: 'Classifications', icon: <ListChecksIcon className="w-6 h-6" /> },
        { view: 'schedules', label: 'Schedules', icon: <CalendarDaysIcon className="w-6 h-6" /> },
    ],
    "System": [
        { view: 'general', label: 'General Settings', icon: <ClockIcon className="w-6 h-6" /> },
        { view: 'displayGroups', label: 'Display Groups', icon: <EmployeesIcon className="w-6 h-6" /> },
        { view: 'screensaver', label: 'Screensaver', icon: <MonitorIcon className="w-6 h-6" /> },
        { view: 'smtp', label: 'SMTP', icon: <MailIcon className="w-6 h-6" /> },
        { view: 'notifications', label: 'Notifications', icon: <BellIcon className="w-6 h-6" /> },
        { view: 'importExport', label: 'Import/Export', icon: <DatabaseIcon className="w-6 h-6" /> },
    ]
};

const viewTitles: Record<string, string> = {
    'employees': 'Employees',
    'classifications': 'Pay Classifications',
    'schedules': 'Schedules',
    'displayGroups': 'Display Groups',
    'general': 'General Settings',
    'screensaver': 'Screensaver',
    'importExport': 'Import/Export',
    'smtp': 'SMTP Settings',
    'notifications': 'Notifications',
};

interface ConfigurationScreenProps {
    initialView?: string | null;
    onNavigated?: () => void;
}

const ConfigurationScreen: React.FC<ConfigurationScreenProps> = ({ initialView, onNavigated }) => {
    const [activeView, setActiveView] = useState('general');
    useEffect(() => {
        if (initialView) {
            const viewToNavigate = initialView === 'settings' ? 'general' : initialView;
            if (viewTitles[viewToNavigate]) {
                setActiveView(viewToNavigate);
            }
            onNavigated?.();
        }
    }, [initialView, onNavigated]);
    const renderView = () => {
        switch (activeView) {
            case 'employees': return <EmployeesSettingsTab />;
            case 'classifications': return <ClassificationsTab />;
            case 'schedules': return <SchedulesSettingsTab />;
            case 'displayGroups': return <DisplayGroupsSettingsTab />;
            case 'general': return <PayrollSettingsTab />;
            case 'screensaver': return <ScreensaverSettingsTab />;
            case 'importExport': return <ImportExportTab />;
            case 'smtp': return <SMTPSettingsTab />;
            case 'notifications': return <NotificationSettingsTab />;
            default: return (
                <div className="flex items-center justify-center h-full">
                    <p className="text-gray-500">Select a category to begin.</p>
                </div>
            );
        }
    };
    return (
        <div className="flex flex-col md:flex-row bg-white dark:bg-gray-800/50 rounded-lg shadow-xl -mx-4">
            <aside className="w-full md:w-64 flex-shrink-0 border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-t-lg md:rounded-l-lg md:rounded-tr-none">
                <div className="p-4 space-y-4">
                    {Object.entries(NAV_SECTIONS).map(([sectionTitle, items]) => (
                        <div key={sectionTitle}>
                            <h3 className="px-3 pb-2 text-sm font-semibold text-gray-500 dark:text-gray-400 tracking-wider">{sectionTitle}</h3>
                            <div className="space-y-1">
                                {items.map(item => (
                                    <ConfigNavItem
                                        key={item.view}
                                        label={item.label}
                                        icon={item.icon}
                                        isActive={activeView === item.view}
                                        onClick={() => setActiveView(item.view)}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </aside>
            <main className="flex-grow">
                <div className="p-4 sm:p-6 md:p-8">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">{viewTitles[activeView]}</h2>
                    {renderView()}
                </div>
            </main>
        </div>
    );
};

export default ConfigurationScreen;